import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.addEventListener('pageshow', this.#handlePageShow);
    this.#knownCount = this.currentCartCount;
    this.#externalChangeObserver.observe(this.refs.cartBubbleCount, {
      characterData: true,
      childList: true,
      subtree: true,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.removeEventListener('pageshow', this.#handlePageShow);
    this.#externalChangeObserver.disconnect();
  }

  /**
   * The count text this instance itself is about to write, set right before
   * renderCartBubble mutates the DOM so #externalChangeObserver can tell its
   * own writes apart from ones it didn't make.
   * @type {string | null}
   */
  #expectedNextText = null;

  /**
   * The count this instance last knew about, from either a real render or an
   * already-rebroadcast external change - distinct from #expectedNextText,
   * which only covers the single write currently in flight. Used to tell a
   * genuine external change apart from the app re-writing a value it already
   * wrote (see #externalChangeObserver below).
   * @type {number}
   */
  #knownCount = 0;

  /**
   * The header and the mobile drawer each render their own cart-icon-
   * component, both sharing the same .cart-bubble__text-count class
   * (snippets/cart-bubble.liquid) - deliberate, so both read from the same
   * cartUpdate event (see header-drawer.liquid). Cart apps rendered via app
   * embed (this store's is Essential Cart Drawer, CLAUDE.md) commonly patch
   * *some* theme's cart badge on add/remove for compatibility with themes
   * they don't otherwise integrate with, by grabbing the first element
   * matching a handful of well-known selectors - including this exact one -
   * with a plain `document.querySelector` and writing to it directly,
   * bypassing every event this component listens for. With two matches in
   * this theme, that only ever reaches whichever instance happens to be
   * first in the DOM (the mobile drawer's, here), leaving the other stuck
   * on its last real value indefinitely - confirmed by decompiling the
   * app's own bundle, which contains a `.cart-bubble__text-count` compat
   * branch using exactly that pattern.
   *
   * Rather than guess at every cart app's own event names, this watches
   * this instance's own count text for changes it didn't make itself and,
   * when it sees one, treats that as the true count and rebroadcasts it as
   * a real cartUpdate so every cart-icon on the page - including this one,
   * redundantly but harmlessly - ends up showing the same number.
   *
   * The app's own compat layer re-writes the badge on every cart-activity
   * signal it sees, including our own rebroadcast - not only when the count
   * actually changed. Rebroadcasting unconditionally on any text mutation
   * therefore fed the app's write straight back into another write of the
   * same value, forever: observed live as an infinite ~250ms cart:update
   * loop (matching the CSS bump's --animation-speed) that replayed
   * cart-bubble--animating's slide-in every cycle, reading as the badge
   * continuously blinking in and out despite the count never actually
   * changing. Comparing against #knownCount - the value as of the last
   * genuine change, not the ephemeral in-flight #expectedNextText - makes a
   * same-value rewrite a no-op and breaks the loop at its source. */
  #externalChangeObserver = new MutationObserver(() => {
    const text = this.refs.cartBubbleCount.textContent.trim();

    if (text === this.#expectedNextText) {
      this.#expectedNextText = null;
      return;
    }

    const count = parseInt(text, 10);

    if (Number.isNaN(count) || count === this.#knownCount) return;

    this.#knownCount = count;
    document.dispatchEvent(new CartUpdateEvent({}, 'external', { itemCount: count, source: 'external-cart-count-sync' }));
  });

  /**
   * A bfcache restore reuses the exact DOM/state from before the user
   * navigated away, which can be stale if the cart changed on another tab
   * or page in between - that's the only case sessionStorage's cart-count
   * is more trustworthy than what's on screen. A fresh document load
   * (including every ordinary navigation) already renders the current cart
   * server-side via {{ cart.item_count }}, so running this unconditionally
   * on every connectedCallback let a leftover cart-count from up to 10
   * seconds ago - eg. an add immediately undone, or a stale entry from
   * before a `/cart/clear` - override an already-correct render.
   * @param {PageTransitionEvent} event
   */
  #handlePageShow = (event) => {
    if (event.persisted) this.ensureCartBubbleIsCorrect();
  };

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    const itemCount = event.detail.data?.itemCount ?? 0;
    const comingFromProductForm = event.detail.data?.source === 'product-form-component';

    this.renderCartBubble(itemCount, comingFromProductForm);
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The number of items in the cart.
   * @param {boolean} comingFromProductForm - Whether the cart update is coming from the product form.
   */
  renderCartBubble = async (itemCount, comingFromProductForm, animate = true) => {
    // If the cart update is coming from the product form, we add to the current cart count, otherwise we set the new cart count

    this.refs.cartBubbleCount.classList.toggle('hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('cart-bubble--animating', itemCount > 0 && animate);

    const nextCount = comingFromProductForm ? this.currentCartCount + itemCount : itemCount;
    this.#expectedNextText = nextCount < 100 ? String(nextCount) : '';
    this.#knownCount = nextCount;
    this.currentCartCount = nextCount;

    this.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );

    if (!animate) return;
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    const sessionStorageCount = sessionStorage.getItem('cart-count');

    if (sessionStorageCount === null) return;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);
      const visibleCount = this.refs.cartBubbleCount.textContent;

      if (value === visibleCount) return;

      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
